import { Addressable, AssetEntity } from '../asset';
import { CompanyEntity } from '../company/company.slice';
import type { TElement } from '@udecode/plate-common';
import { UsersEntity } from '../users';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import { MediaInfo } from '../images/types';

export interface ClassificationItemPayload {
id: number;
name: string;
parent_id: number | null;
}

export type ClassificationType = 'category' | 'subcategory' | 'type' | 'subtype';

export interface ClassificationItem extends ClassificationItemPayload {
  level: number;
  type: ClassificationType;
  parents: string[];
  description?: string;
  children: ClassificationItem[];
}


export interface ReportBlock {
  id: number;
  organisation_id: number;
  organisation?: CompanyEntity;
  title: string;
  name: string;
  description: string;
  body: TElement[];
  created_by: number;
  updated_by: number;
  retired?: boolean;
  status: 'active' | 'archived';
  is_globally_archived: boolean;
  created_at: string;
  updated_at: string;
  permittedUse: string[];
}

export interface Collaborator {
  id: string;
  name: string;
  avatar: string;
}

export interface ReportCounts {
  total: number;
  completed: number;
  inProgress: number;
}

export const reportTypes = [
  'portfolio',
  'asset',
  'inspection',
  'defects',
  'transfer',
] as const;

export type ReportType = (typeof reportTypes)[number];

// Internal Address type for services - not exported to avoid conflict with asset Address
interface BillingAddress {
  id: number;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  created_at: string;
  updated_at: string;
}

export interface Client {
  id: number;
  first_name: string | null;
  last_name: string | null;
  position: string | null;
  phone: string | null;
  organisation_name: string;
  email: string | null;
  logo: MediaInfo | null;
  logo_thumb: string | null;
  logo_preview: string | null;
  profile?: MediaInfo | null;
  address?: Partial<Addressable>; // Optional, as it is loaded conditionally
  created_at: string;
  updated_at: string;
}

export type ReportAuthor = UsersEntity & { isPrimary: boolean };

export type ReportSectionConfig = {
  includeIntroduction: boolean;
  includeSummary: boolean;
  // includeSummaryAnalysis: boolean;
  includeResults: boolean;
  includeTerms: boolean;
}

export type ReportAccessType = 'owned' | 'shared' | 'saved';

/** Report Format (ADR-0017, api repo) — mirrors ReportFormatGatingService::FORMAT_SDA. */
export const REPORT_FORMAT_SDA = 'sda' as const;
export type ReportFormat = typeof REPORT_FORMAT_SDA | null;

export interface ReportEntity {
  id: string;
  title: string;
  description: string | null;
  status: 'active' | 'archived';
  retired?: boolean;
  createdAt: string;
  assets: AssetEntity[];
  assessments: any[];
  collaborators: ReportAuthor[];
  type: ReportType;
  reportCounts: ReportCounts;
  is_globally_archived?: boolean;
  client: Client;
  creator: UsersEntity;
  testSuites: TestSuite[];
  sections: ReportSection[];
  section_config?: ReportSectionConfig;
  revisions: ReportRevision[];
  hasDraftRevision?: boolean;
  access_type?: ReportAccessType;
  /** Report Format — null/absent for the standard layout. */
  format?: ReportFormat;
}

export interface ReportUpdatePayload {
  title: string;
  description: string;
  client: Partial<Client>;
  collaborators: {
    id: number;
    isPrimary: boolean;
  }[];
  retired?: boolean;
  inspections?: number[];
  assets?: number[];
  section_config?: Partial<ReportSectionConfig>;
}

export type ReportLinkVisitor = {
  id: number;
  email: string;
  name: string;
  is_registered_user: boolean;
  last_accessed: string;
}

export type ReportLink = {
  id: number;
  url: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  access_count?: number;
  allow_share: boolean;
  allow_export: boolean;
  visitors: ReportLinkVisitor[];
}

export type ReportRevision = {
  id: number;
  report_id: number;
  version: string;
  status: 'draft' | 'published' | 'superseded' | 'withdrawn';
  notes: string | null;
  created_at: string;
  updated_at: string;
  creator?: UsersEntity;
  report?: Report;
  test_items?: TestItem[];
  link?: ReportLink;
  link_enabled?: boolean;
  saved_link?: {
    access_count: number;
    allow_export: boolean;
    allow_share: boolean;
    created_at: string;
    id: number;
    is_enabled: boolean;
    updated_at: string;
  }
}


export interface ReportPayload {
  file?: File | null;
  // other report fields
  [key: string]: any;
}

export interface ReportResponse {
  id: string;
  // other response fields
  [key: string]: any;
}

export interface ReportPermissions {
  isCollaborator: boolean;
  isOrganisationMember: boolean;
  isAnonymous: boolean;
  isIssueAccess: boolean;
  isPublicLink: boolean;
  isSaved: boolean;
  allow_save: boolean;
  allow_share: boolean;
  allow_export: boolean;
  guestHasAccount: boolean;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    has_account: boolean;
    is_temporary: boolean;
  }
}

export interface PublishedReportResponse {
  revision: ReportRevision;
  published: PublishedReport;
  permissions: ReportPermissions;
}

export type ReportSection = {
  id: string;
  report_id?: number;
  original_id?: number;
  revision_id?: number;
  content: string;
  position: string;
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
};

export interface ToggleSwitchResponse {
  success: boolean;
  value: boolean;
}

export interface ToggleSwitchRequest {
  reportId: string;
  revisionId: string;
  testItemId: string;
  switchName: 'include' | 'fast_fill_accepted';
}

export interface FastFillToggleResponse {
  success: boolean;
  value: boolean;
  updated_at: string;
  updated_by: number;
}

export interface FastFillToggleRequest {
  testItemId: string;
}

export type ExclusionReasons = 'manuallyexcluded' | 'status' | 'unmatchedsegments' | 'missingdata' | 'included';

export interface TestItemComment {
  id: number;
  content: string;
  type: string;
  created_at: string;
  creator?: UsersEntity;
  can_delete?: boolean;
  resolved_at: string | null;
  resolved_by: number | null;
  resolver?: UsersEntity;
}

export type ReportTestItem = TestItem & {
  reportId: number;
  author: UsersEntity;
  asset_name: string;
  building_description: string;
  level_description: string;
  area_description: string;
  score: number;
  testSpecification: TestSpecification;
  element: Element;
  ratingType: RatingTypes;
  custom?: Record<string, string>;
  groupId?: string;
  hasUnmatchedSegments?: boolean;
  hasMissingData?: boolean;
  hasFastFillQA?: boolean;
  exclusionReason?: ExclusionReasons;
  revision?: TestRevision;
  switches?: TestSwitches;
};

export type RatingTypes =
  | 'compliance'
  | 'compliance_b'
  | 'compliance_a'
  | 'condition'
  | 'mixed'
  | 'frankenstein'
  | 'no_rating'
  | 'generic'
  | 'unknown';

export type TestRevision = {
  report_revision_id? : number; // report_revision_id
  original_id? : number;
  revision_id? : number;
}

export type TestSwitches = {
  include?: boolean;
  fast_fill_accepted?: boolean;
}

export type TestSpecification = TestCase & {
  test_suite_title: string;
  type: 'compliance' | 'condition' | 'generic';
  testSuiteId: number;
};

export interface DashboardRecentReport {
  id: number;
  title: string;
  client_name?: string;
  updated_at?: string;
}

export interface DashboardRecentInspection {
  id: number;
  title: string;
  asset_name?: string;
  updated_at?: string;
}

export interface DashboardRecentResponse {
  reports: DashboardRecentReport[];
  inspections: DashboardRecentInspection[];
}

export interface ActivityItem {
  id: number;
  description: string;
  causer_name: string;
  causer_avatar?: string;
  subject_type: string;
  subject_id?: number | string | null;
  created_at: string;
  event: string;
}

export interface AddCustomFieldOptionRequest {
  testSuiteId: number | string;
  id?: string; // uuid
  reportId?: number;
  field_id: string;
  displayName: string;
}

export interface AddCustomFieldOptionResponse {
  message: string;
  field_id: string;
  data: Array<{
    id: string;
    displayName: string;
  }>;
}

export interface PublishReportPayload {
  report: ReportEntity;
  items: ReportTestItem[];
  notes: string;
  publishOption: 'keep' | 'supersede' | 'withdraw';
  revision: ReportRevision;
  dry_run?: boolean;
}

export interface PublishReportResponse {
  revision: ReportRevision;
  published: PublishedReport;
  previous_recipients?: {
    email: string;
    first_name: string;
    last_name: string;
    user_id: number;
    previous_allow_share: boolean;
    previous_allow_export: boolean;
    last_revision_id: number;
    last_revision_created_at: string;
    last_revision_version: string;
  }[];
}

export interface PublishedReport {
  report: ReportEntity;
  items: ReportTestItem[];
  created_at: string;
  updated_at: string;
  creator: UsersEntity;
  id: number; // Published Report ID
  report_revision_id: number; // Report Revision ID
  permissions: ReportPermissions;
}

export type ReportIssue = {
  id: number;
  report_revision_id: number;
  created_at: string;
  updated_at: string;
  issuer: UsersEntity;
  email: string;
  name: string;
}

export interface ShareReportRequest {
  user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  allow_share?: boolean;
  allow_export?: boolean;
}

export interface ShareReportResponse {
  message: string;
  data: {
    result: {
      // Add specific result fields based on your API response
      id: string;
      // ... other fields
    };
    invite?: {
      id: string;
      token: string;
      // ... other invite fields
    };
  };
}

export interface BulkIssueResponse {
  summary: {
    total: number
    successful: number
    skipped: number
    failed: number
  }
}

export interface FastFillUpdateRequest {
  type: 'observations' | 'recommendations';
  description: string;
}

export interface BulkUpdateItemResponse {
  id: number;
  status: string;
  success: boolean;
  error?: string;
}

export interface GetTestItemRequest {
  testItemId: number;
  reportId?: number;
}

// Billing Types
export interface BillingStatus {
  is_subscribed: boolean
  stripe_status: 'active' | 'trialing' | 'past_due' | 'perpetual' | null
  current_plan: string | null // Stripe price ID (e.g., "price_1SXz6bGWLmfkWSziLiI5MKUY")
  plan_name: string | null // Human-readable plan name (e.g., "Pro Plan")
  status: string | null // Human-readable status (e.g., "active", "trialing")
  seat_count: number
  trial_ends_at: string | null // ISO Date
  on_trial: boolean
  trial_days_left: number | null
  cardless_trial: boolean
  seats_limit?: number | null
  seats_used?: number | null
  cancellation_pending?: boolean // True when subscription is scheduled to cancel
  subscription_ends_at?: string | null // ISO Date when subscription will end (if cancelled)
}

// Base Organisation (Public/Generic) - billing is NOT guaranteed
export interface Organisation {
  id: number
  title: string
  logo_url?: string
  role?: string // Current user's role if known
}

// Billing Contact/Detail Types
// Matches backend API: /api/v1/organisations/{organisation}/billing/contact
export interface BillingDetail {
  id: number
  organisation_id: number
  company_name: string | null
  billing_email: string // Required
  email_ccs: string[] // Array of email addresses
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null // Required for AU
  postal_code: string | null
  country: 'AU' | 'NZ' | 'UK' | 'US' // Required, limited to these 4 countries
  tax_id_type: 'abn' | 'gst' | 'vat' | 'ein' | null
  tax_id_value: string | null // Format validated per country/type
  po_number: string | null
  peppol_id: string | null
  created_at: string | null
  updated_at: string | null
}

// Billing-Aware Organisation (For Admin/Settings)
// This matches the new 'OrganisationBillingResource' from the backend
// Extends Organisation and adds billing-specific fields
export interface OrganisationWithBilling extends Organisation {
  billing: BillingStatus
  license_type: 'free' | 'paid' // Current user's specific license
  license_status: 'active' | 'cancellation_pending'
  license_ends_at: string | null
  billing_detail?: BillingDetail | null // Optional billing contact details
}

// Type guard to check if an organisation has billing info
export function isOrganisationWithBilling(
  org: Organisation | OrganisationWithBilling
): org is OrganisationWithBilling {
  return 'billing' in org && 'license_type' in org
}

// Billing API Request/Response Types
export interface CreateCheckoutSessionRequest {
  organisationId: number
  plan?: string
  success_url?: string
  cancel_url?: string
}

export interface CreateCheckoutSessionResponse {
  session_id: string
  url?: string
  checkout_url?: string
}

export interface CreatePortalSessionRequest {
  organisationId: number
  return_url?: string
}

export interface CreatePortalSessionResponse {
  url?: string
  portal_url?: string // Alternative field name some backends might use
}

export interface UpdateUserLicenseRequest {
  organisationId: number
  userId: number
  license_type: 'free' | 'paid'
}

export interface UpdateUserLicenseResponse {
  user_id: number
  license_type: 'free' | 'paid'
  license_status: 'active' | 'cancellation_pending'
  license_ends_at: string | null
}

export interface PreviewLicenseChangeRequest {
  organisationId: number
  userId: number
  license_type: 'free' | 'paid'
}

export interface PreviewLicenseChangeData {
  immediate_charge: number // Total including tax (dollars)
  immediate_charge_subtotal: number // Subtotal before tax (dollars)
  immediate_charge_tax: number // Tax amount (dollars)
  recurring_increase: number // Base price per seat (dollars) - cost added to next bill
  recurring_increase_tax: number | null // Tax on recurring (null - calculated at billing time)
  current_seats: number // Current number of paid seats
  new_total_seats: number // Total seats after upgrade (current + 1)
  total_cost_per_month: number // Total monthly cost for all seats (dollars)
  next_billing_date: string | null // Next billing cycle date (ISO 8601 format)
  currency: string
  interval: string // 'month' or 'year'
  description: string
  next_payment_date?: string | null // Deprecated - use next_billing_date
}

export interface PreviewLicenseChangeResponse {
  data: PreviewLicenseChangeData | null
}

export interface Invoice {
  date: string // ISO 8601 date string
  total: string // Formatted string like "$10.00"
  status: string // e.g., "paid", "open", "void"
  pdf_url: string | null // Direct link to PDF invoice
}

// Backend returns array of invoices directly
export type GetInvoicesResponse = Invoice[]
